import { useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton } from '@atlaskit/button-next/new';
import { NewCoreIconProps } from '@atlaskit/icon';
import CopyIcon from '@atlaskit/icon/core/copy';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';

import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';

import { useAISummaryContext } from './ai-summary-context';

type FeedbackType = 'thumbsUp' | 'thumbsDown' | null;

const aiSummaryFooterStyle = css({
	display: 'flex',
	justifyContent: 'space-between',
	padding: token('space.050'),
	marginBlockEnd: token('space.negative.200'),
});

const aiSummaryFooterSectionStyle = css({
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	gap: token('space.200'),
	fontSize: '12px',
	lineHeight: token('space.200'),
});

const aiFeedbackSectionStyle = css({
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'flex-end',
});

const disclaimerTextStyle = css({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.050'),
});

const subtleLinkOverrideStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& a': {
		color: `${token('color.text.subtlest')}`,
	},
});

const FeedbackThumpsUpIcon = (iconProps: NewCoreIconProps, feedbackState: FeedbackType) => {
	return (
		<ThumbsUpIcon
			{...iconProps}
			color={feedbackState === 'thumbsUp' ? token('color.icon.selected') : undefined}
		/>
	);
};

const FeedbackThumpsDownIcon = (iconProps: NewCoreIconProps, feedbackState: FeedbackType) => {
	return (
		<ThumbsDownIcon
			{...iconProps}
			color={feedbackState === 'thumbsDown' ? token('color.icon.selected') : undefined}
		/>
	);
};

// eslint-disable-next-line complexity
export const AISummaryFooter = () => {
	const [feedbackState, setFeedbackState] = useState<FeedbackType>(null);

	const intl = useIntl();
	const aiHref = useLocalizedHref('/trust/atlassian-intelligence');
	const policyHref = useLocalizedHref('/legal/acceptable-use-policy');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { summary, likeFeedback, dislikeFeedback } = useAISummaryContext();

	const handleCopy = async () => {
		try {
			// Remove citation markers like [1], [2], etc. from the text
			const textToCopy = summary.replace(/\[\d+\]/g, '').trim();
			await navigator.clipboard.writeText(textToCopy);

			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'searchAiSummaryCopyText',
				source: 'searchAiSummary',
				attributes: {
					product: 'search',
				},
			}).fire();
		} catch (err) {
			console.error('Failed to copy text:', err);
		}
	};

	const handleFeedbackClick = (state: FeedbackType) => {
		setFeedbackState(state);
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'searchAiSummaryFeedback',
			source: 'searchAiSummary',
			attributes: {
				feedback: state,
				product: 'search',
			},
		}).fire();

		if (state === 'thumbsUp') {
			likeFeedback();
		} else {
			dislikeFeedback();
		}
	};

	const handleDisclaimerClick = (label: string, labelEnglish: string, href: string) => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'searchAiSummaryDisclaimer',
			source: 'searchAiSummary',
			attributes: {
				label,
				labelEnglish,
				product: 'search',
				href,
			},
		}).fire();
	};

	const aiDisclaimerText = intl.formatMessage({
		id: 'ai.summary.answer.footer.disclaimer',
		defaultMessage: 'Uses AI. Verify results.',
	});

	const aiPolicyText = intl.formatMessage({
		id: 'ai.summary.answer.footer.policy',
		defaultMessage: 'Subject to Atlassian Acceptable Use Policy',
	});

	const thumbsUpText = intl.formatMessage({
		id: 'ai.summary.answer.footer.thumbsUp',
		defaultMessage: 'Good response',
	});

	const thumbsDownText = intl.formatMessage({
		id: 'ai.summary.answer.footer.thumbsDown',
		defaultMessage: 'Bad response',
	});

	const copyText = intl.formatMessage({
		id: 'ai.summary.answer.footer.copy',
		defaultMessage: 'Copy',
	});

	return (
		<div css={aiSummaryFooterStyle}>
			<div css={aiSummaryFooterSectionStyle}>
				<span css={[disclaimerTextStyle, subtleLinkOverrideStyle]}>
					<InformationCircleIcon color={token('color.icon.subtlest')} size="medium" label="" />
					<Link
						href={aiHref}
						aria-label={aiDisclaimerText}
						onClick={() =>
							handleDisclaimerClick(aiDisclaimerText, 'Uses AI. Verify results.', aiHref)
						}
						target="_blank"
						appearance="subtle"
					>
						{aiDisclaimerText}
					</Link>
				</span>
				<span css={subtleLinkOverrideStyle}>
					<Link
						href={policyHref}
						aria-label={aiPolicyText}
						onClick={() =>
							handleDisclaimerClick(
								aiPolicyText,
								'Subject to Atlassian Acceptable Use Policy',
								policyHref,
							)
						}
						target="_blank"
						appearance="subtle"
					>
						{aiPolicyText}
					</Link>
				</span>
			</div>
			<div css={aiFeedbackSectionStyle}>
				<IconButton
					appearance="subtle"
					icon={(iconProps) => FeedbackThumpsUpIcon(iconProps, feedbackState)}
					label={thumbsUpText}
					onClick={() => handleFeedbackClick('thumbsUp')}
				/>
				<IconButton
					appearance="subtle"
					icon={(iconProps) => FeedbackThumpsDownIcon(iconProps, feedbackState)}
					label={thumbsDownText}
					onClick={() => handleFeedbackClick('thumbsDown')}
				/>
				<IconButton appearance="subtle" icon={CopyIcon} label={copyText} onClick={handleCopy} />
			</div>
		</div>
	);
};
